import React, { useState, useMemo } from "react";
import { useQuery, QueryClient, QueryClientProvider } from "react-query";
import { Popover, Input, Button, Select, Checkbox } from "antd";
import { MdAddCircle } from "react-icons/md";
import Container from "../../components/Layout/Container";
import ContentBlock from "../../components/Layout/ContentBlock";
import FilterMenu from "../../components/Layout/FilterMenu";
import {
    Pagination,
    TextInput,
    SelectInput,
} from "../../components/globals";
import TableFilterBox from "../../components/common/TableFilterBox";
import {
    antdAsyncMessageError,
    antdAsyncMessageSuccess,
} from "../../helpers/AsyncMessage";
import axios from "axios";
import ContentValidation from "../../components/common/ContentValidation";
import { IconVVStockProduct } from "../../components/Layout/Icons";
import useDebounce from "../../hooks/useDebounce";
import dayjs from "dayjs";

import { DEFAULT_PER_PAGE } from "../../helpers/default";
const MigrateOutPopover = ({ productSupplier, onSave, supplierName, productId, hasPendingQuantity, hasQuantity, selectedDestinationSupplier, productSuppliers }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [deptFilter, setDeptFilter] = useState("");
    const [qtyErrorIds, setQtyErrorIds] = useState(new Set());

    const pendingQty = productSupplier?.pending_quantity || 0;
    const availableQty = Math.max(0, (productSupplier?.quantity || 0) - pendingQty);

    const fetchDepartments = async () => {
        setDepartmentsLoading(true);
        try {
            const response = await axios.get('/api/v1/suppliers', {
                params: { 'q[name_cont]': 'vv_', per_page: 200 }
            });
            const list = response.data?.data || [];
            const sourceId = productSupplier?.supplier_id || productSupplier?.id;
            const filtered = list.filter(s => s.id !== sourceId);
            const preSelectedId = selectedDestinationSupplier?.id;
            const sorted = preSelectedId
                ? [...filtered.filter(s => s.id === preSelectedId), ...filtered.filter(s => s.id !== preSelectedId)]
                : filtered;
            setDepartments(
                sorted.map(s => ({
                    id: s.id,
                    label: s.title || s.name || s.code,
                    quantity: "",
                    checked: s.id === preSelectedId,
                    specialTransfer: false,
                }))
            );
        } catch (err) {
            console.error('Error fetching departments', err);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    const handleVisibilityChange = (visible) => {
        setPopoverVisible(visible);
        if (visible) {
            fetchDepartments();
            setDeptFilter("");
            setQtyErrorIds(new Set());
        }
    };

    const toggleDepartment = (id) => {
        setDepartments(prev => prev.map(d => d.id === id ? { ...d, checked: !d.checked } : d));
    };

    const getRemainingSourceQty = (excludeId) => {
        const usedByOthers = departments
            .filter(d => d.checked && d.id !== excludeId)
            .reduce((sum, d) => sum + (parseFloat(d.quantity) || 0), 0);
        return Math.max(0, availableQty - usedByOthers);
    };

    const setDepartmentQty = (id, qty) => {
        const parsed = parseFloat(qty);
        const remaining = getRemainingSourceQty(id);
        let clamped = qty;
        if (!isNaN(parsed)) {
            if (remaining <= 0) {
                clamped = "";
            } else if (parsed > remaining) {
                clamped = String(remaining);
            }
        }
        setDepartments(prev => prev.map(d => d.id === id ? { ...d, quantity: clamped } : d));
        setQtyErrorIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    };

    const toggleDepartmentSpec = (id) => {
        setDepartments(prev => prev.map(d => d.id === id ? { ...d, specialTransfer: !d.specialTransfer } : d));
    };

    const handleSave = async () => {
        const selected = departments.filter(d => d.checked);
        if (selected.length === 0) {
            antdAsyncMessageError({ text: "Please select at least one department" });
            return;
        }

        const invalids = selected.filter(d => !d.quantity || parseFloat(d.quantity) <= 0);
        if (invalids.length > 0) {
            setQtyErrorIds(new Set(invalids.map(d => d.id)));
            antdAsyncMessageError({ text: `Please enter a valid quantity for: ${invalids.map(d => `"${d.label}"`).join(", ")}` });
            return;
        }

        setQtyErrorIds(new Set());

        const payload = {
            sender_id: productSupplier.supplier_id || productSupplier.id,
            product_id: productId,
            transfers: selected.map(d => ({
                receiver_id: d.id,
                quantity: parseFloat(d.quantity),
                special_transfer: d.specialTransfer,
            })),
        };

        setLoading(true);
        try {
            await axios.post('/api/v1/product_transfers/create_items_bulk', payload);
            antdAsyncMessageSuccess({ text: `Migration created for ${selected.length} department(s)` });
            setPopoverVisible(false);
            if (onSave) onSave();
        } catch (error) {
            console.error("Migration error:", error);
            const errorMessage = error?.response?.data?.error || "Failed to create product migration";
            antdAsyncMessageError({ text: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const selectedCount = departments.filter(d => d.checked).length;
    const filteredDepartments = deptFilter.trim()
        ? departments.filter(d => d.label.toLowerCase().includes(deptFilter.toLowerCase()))
        : departments;

    const content = (
        <div style={{ width: 360 }}>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#555' }}>
                From: <strong>{supplierName}</strong>
            </div>

            <Input
                placeholder="Filter departments..."
                value={deptFilter}
                onChange={e => setDeptFilter(e.target.value)}
                size="small"
                style={{ marginBottom: 8 }}
                allowClear
            />

            {departmentsLoading ? (
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#999' }}>Loading departments...</div>
            ) : filteredDepartments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#999' }}>No departments found</div>
            ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 10 }}>
                    {/* column headers */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, paddingRight: 4, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <span style={{ flex: 1, fontSize: 11, color: '#999' }}>Department</span>
                        <span style={{ width: 70, fontSize: 11, color: '#999', textAlign: 'center' }}>QTY</span>
                        <span style={{ width: 46, fontSize: 11, color: '#999', textAlign: 'center' }}>Spec. Delivery</span>
                    </div>
                    {filteredDepartments.map(dept => (
                        <div key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingRight: 4 }}>
                            <Checkbox
                                checked={dept.checked}
                                onChange={() => toggleDepartment(dept.id)}
                                style={{ flex: 1 }}
                            >
                                <span style={{ fontSize: 13 }}>{dept.label}</span>
                                {(() => {
                                    const ps = productSuppliers?.find(s => s.id === dept.id);
                                    if (!ps) return null;
                                    const qty = ps.pending_quantity > 0 ? `${ps.pending_quantity}/${ps.quantity}` : ps.quantity;
                                    return <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>({qty})</span>;
                                })()}
                            </Checkbox>
                            <Input
                                type="number"
                                value={dept.quantity}
                                onChange={e => setDepartmentQty(dept.id, e.target.value)}
                                style={{ width: 70, opacity: dept.checked ? 1 : 0.35, borderColor: qtyErrorIds.has(dept.id) ? '#ff4d4f' : undefined }}
                                min="1"
                                step="1"
                                max={getRemainingSourceQty(dept.id) || undefined}
                                placeholder="QTY"
                                size="small"
                                disabled={!dept.checked}
                            />
                            <Checkbox
                                checked={dept.specialTransfer}
                                onChange={() => toggleDepartmentSpec(dept.id)}
                                disabled={!dept.checked}
                                style={{ width: 46, justifyContent: 'center' }}
                            />
                        </div>
                    ))}
                </div>
            )}

            <Button
                type="primary"
                onClick={handleSave}
                loading={loading}
                disabled={selectedCount === 0 || loading}
                style={{ width: '100%' }}
            >
                {selectedCount > 0 ? `Migrate to ${selectedCount} department(s)` : 'Select departments'}
            </Button>
        </div>
    );

    return (
        <Popover
            content={content}
            title="Migrate Product Out"
            open={popoverVisible}
            onOpenChange={handleVisibilityChange}
            trigger="click"
            placement="right"
        >
            <MdAddCircle
                color={hasPendingQuantity ? "#0066CC" : hasQuantity ? "#28a745" : "#03B0F7"}
                size={16}
                style={{ cursor: "pointer", marginLeft: "5px" }}
                title={`Migrate product out from ${supplierName}`}
            />
        </Popover>
    );
};

export const queryVVStockProductsClient = new QueryClient();

const VVStockProductsPageContent = () => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [totals, setTotals] = useState(0);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [sortSupplier, setSortSupplier] = useState(null); // supplier id to sort by
    const [sortDirection, setSortDirection] = useState(null); // 'asc' or 'desc'
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedDestinationSupplier, setSelectedDestinationSupplier] = useState(null);
    const [pickupAddresses, setPickupAddresses] = useState([]);
    const [pickupSearchText, setPickupSearchText] = useState("");
    const [showPickupDropdown, setShowPickupDropdown] = useState(false);
    const [brands, setBrands] = useState([]);
    const [brandSearchText, setBrandSearchText] = useState("");
    const [debouncedBrandSearchText, setDebouncedBrandSearchText] = useState("");
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);

    const [inputSku, setInputSku] = useState("");
    const [inputVVSku, setInputVVSku] = useState("");
    const [inputTitle, setInputTitle] = useState("");
    const [inputCategory, setInputCategory] = useState([]);
    const [inputBrand, setInputBrand] = useState([]);
    const [inputSupplierFilter, setInputSupplierFilter] = useState([]);
    const [inputOldStockFilter, setInputOldStockFilter] = useState("");

    const [filterSku, setFilterSku] = useState("");
    const [filterVVSku, setFilterVVSku] = useState("");
    const [filterTitle, setFilterTitle] = useState("");
    const [filterCategory, setFilterCategory] = useState([]);
    const [filterBrand, setFilterBrand] = useState([]);
    const [filterSupplierFilter, setFilterSupplierFilter] = useState([]);
    const [filterOldStockFilter, setFilterOldStockFilter] = useState("");

    const fetchPickupAddresses = async (searchText = "", page = 1, perPage = 5) => {
        try {
            let url = '/api/v1/pickup_addresses';
            const params = new URLSearchParams();

            if (searchText.trim()) {
                params.append('search', searchText);
            }
            params.append('page', page.toString());
            params.append('per_page', perPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url);
            const data = response.data;
            if (Array.isArray(data)) {
                return data;
            } else if (data && Array.isArray(data.data)) {
                return data.data;
            } else if (data && typeof data === 'object') {
                return [data];
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error fetching pickup addresses:', error);
            return [];
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('/api/v1/products/for_transfer_categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    };

    const fetchBrands = async (searchText = "", page = 1, perPage = 20) => {
        try {
            let url = '/api/v1/products/for_transfer_brands';
            const params = new URLSearchParams();

            if (searchText.trim()) {
                params.append('q[title_i_cont_any]', searchText);
            }
            params.append('page', page.toString());
            params.append('per_page', perPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url);
            const data = response.data;
            if (Array.isArray(data)) {
                return data;
            } else if (data && Array.isArray(data.data)) {
                return data.data;
            } else if (data && typeof data === 'object') {
                return [data];
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
            return [];
        }
    };

    const fetchSuppliers = async (searchText = "", page = 1, perPage = 100) => {
        try {
            let url = '/api/v1/suppliers';
            const params = new URLSearchParams();

            if (searchText.trim()) {
                params.append('search', searchText);
            }
            params.append('page', page.toString());
            params.append('per_page', perPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url);
            const data = response.data;
            if (Array.isArray(data)) {
                return data;
            } else if (data && Array.isArray(data.data)) {
                return data.data;
            } else if (data && typeof data === 'object') {
                return [data];
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            return [];
        }
    };

    const handleSupplierQuantityUpdate = (productId, supplierId, newQuantity) => {
        refetchProducts();
    };

    const fetchProducts = async () => {
        let url = `/api/v1/products/for_transfer?page=${page}&per_page=${perPage}`;

        if (filterSku) {
            url += `&q[identifier_eq]=${encodeURIComponent(filterSku)}`;
        }

        if (filterVVSku) {
            url += `&q[vv_sku_eq]=${encodeURIComponent(filterVVSku)}`;
        }

        if (filterTitle) {
            url += `&q[title]=${encodeURIComponent(filterTitle)}`;
        }

        if (filterOldStockFilter) {
            url += `&q[old_stock]=${encodeURIComponent(filterOldStockFilter)}`;
        }


        if (Array.isArray(filterCategory) && filterCategory.length > 0) {
            filterCategory.forEach(categoryId => {
                url += `&q[category_id_in][]=${categoryId}`;
            });
        }

        if (Array.isArray(filterBrand) && filterBrand.length > 0) {
            filterBrand.forEach(brandId => {
                url += `&q[brand_id_in][]=${brandId}`;
            });
        }

        if (Array.isArray(filterSupplierFilter) && filterSupplierFilter.length > 0) {
            filterSupplierFilter.forEach(supplierId => {
                url += `&supplier_ids[]=${supplierId}`;
            });
        }

        if (sortSupplier && sortDirection) {
            url += `&sort_supplier_id=${sortSupplier}&sort_direction=${sortDirection}`;
        }

        try {
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching products:', error);
            return {
                data: [],
                suppliers: [],
                totals: 0
            };
        }
    };

    // Debounce brand search
    useDebounce(
        () => {
            setDebouncedBrandSearchText(brandSearchText);
        },
        500,
        [brandSearchText]
    );

    const {
        data: pickupAddressesData,
        isLoading: pickupAddressesLoading,
    } = useQuery(
        ["pickup_addresses", pickupSearchText],
        () => fetchPickupAddresses(pickupSearchText, 1, 5),
        {
            enabled: showPickupDropdown || pickupSearchText.length > 0,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const {
        data: categoriesData,
        isLoading: categoriesLoading,
    } = useQuery(
        ["vv_stock_categories"],
        fetchCategories,
        {
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const {
        data: brandsData,
        isLoading: brandsLoading,
    } = useQuery(
        ["brands", debouncedBrandSearchText],
        () => fetchBrands(debouncedBrandSearchText, 1, 20),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const {
        data: products,
        isLoading: productsLoading,
        isFetching: productsFetching,
        error: productsError,
        refetch: refetchProducts,
    } = useQuery(
        ["vv_stock_products", page, perPage, filterSku, filterVVSku, filterTitle, filterCategory, filterBrand, filterSupplierFilter, sortSupplier, sortDirection, filterOldStockFilter],
        fetchProducts,
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    const handleFilterChange = () => {
        setFilterSku(inputSku);
        setFilterVVSku(inputVVSku);
        setFilterTitle(inputTitle);
        setFilterCategory(inputCategory);
        setFilterBrand(inputBrand);
        setFilterSupplierFilter(inputSupplierFilter);
        setFilterOldStockFilter(inputOldStockFilter);
        setPage(1);
    };

    const handleFilterClear = () => {
        setInputSku("");
        setInputVVSku("");
        setInputTitle("");
        setInputCategory([]);
        setInputBrand([]);
        setInputSupplierFilter([]);
        setFilterSku("");
        setFilterVVSku("");
        setFilterTitle("");
        setFilterCategory([]);
        setFilterBrand([]);
        setFilterSupplierFilter([]);
        setPickupSearchText("");
        setShowPickupDropdown(false);
        setBrandSearchText("");
        setDebouncedBrandSearchText("");
        setShowBrandDropdown(false);
        setSelectedSupplier(null);
        setSelectedDestinationSupplier(null);
        setInputOldStockFilter("");
        setFilterOldStockFilter("");
        setPage(1);
    };

    React.useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key === 'Enter' && !e.target.closest('.ant-select-dropdown')) {
                handleFilterChange();
            }
        };
        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, [inputSku, inputVVSku, inputTitle, inputCategory, inputBrand, inputSupplierFilter, inputOldStockFilter]);

    const toggleRowExpansion = (productId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    const filteredPickupOptions = useMemo(() => {
        if (!pickupAddressesData || !Array.isArray(pickupAddressesData)) return [{ label: "All", value: "" }];

        return [
            { label: "All", value: "" },
            ...pickupAddressesData.map(address => ({
                label: address.name || address.address || `Address ${address.id}`,
                value: address.id || address.address || address.id?.toString() || ""
            }))
        ];
    }, [pickupAddressesData]);

    const categoryOptions = useMemo(() => {
        if (!categoriesData || !Array.isArray(categoriesData)) return [{ label: "All categories", value: "" }];

        const sortedCategories = [...categoriesData]
            .map(cat => ({
                label: cat.name,
                value: cat.id
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return [
            { label: "All categories", value: "" },
            ...sortedCategories
        ];
    }, [categoriesData]);

    const filteredBrandOptions = useMemo(() => {
        if (!brandsData || !Array.isArray(brandsData)) return [{ label: "All", value: "" }];

        return [
            { label: "All", value: "" },
            ...brandsData.map(brand => ({
                label: brand.title || `Brand ${brand.id}`,
                value: brand.id || brand.id?.toString() || ""
            }))
        ];
    }, [brandsData]);

    const handleSortSupplier = (supplierId) => {
        setPage(1);
        if (sortSupplier === supplierId) {
            if (sortDirection === 'desc') {
                setSortDirection('asc');
            } else if (sortDirection === 'asc') {
                setSortSupplier(null);
                setSortDirection(null);
            }
        } else {
            setSortSupplier(supplierId);
            setSortDirection('desc');
        }
    };

    const filteredData = useMemo(() => {
        if (!products?.data) return [];
        return products.data;
    }, [products?.data]);

    const visibleSuppliers = useMemo(() => {
        if (!suppliers || suppliers.length === 0) return [];
        return suppliers;
    }, [suppliers]);

    React.useEffect(() => {
        if (products?.totals !== undefined) {
            setTotals(products.totals);
        }
        if (products?.suppliers) {
            setSuppliers(products.suppliers);
        }
    }, [products]);

    React.useEffect(() => {
        if (pickupAddressesData && Array.isArray(pickupAddressesData)) {
            setPickupAddresses(pickupAddressesData);
        }
    }, [pickupAddressesData]);

    React.useEffect(() => {
        if (brandsData && Array.isArray(brandsData)) {
            setBrands(brandsData);
        }
    }, [brandsData]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (showPickupDropdown && !event.target.closest('[name="pickup_location_search"]') && !event.target.closest('.pickup-dropdown')) {
                setShowPickupDropdown(false);
            }
            if (showBrandDropdown && !event.target.closest('[name="brand_search"]') && !event.target.closest('.brand-dropdown')) {
                setShowBrandDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showPickupDropdown, showBrandDropdown]);

    return (
        <Container
            location={{
                locationName: "Products",
                locationUrl: "/vv-stock/products"
            }}
            breadcrumbs={[
                { label: "VV Stock", value: "/vv-stock" },
                { label: "Products", value: "/vv-stock/products" }
            ]}
        >
            <>
                <ContentBlock>
                    <FilterMenu
                        totals={totals}
                        length={products?.data?.length || 0}
                        onChangeCountPage={setPerPage}
                        countPage={perPage}
                        text2="products"
                        iconComponent={<IconVVStockProduct />}
                        page={page}
                    />
                </ContentBlock>

                <ContentBlock>
                    <TableFilterBox
                        filter={true}
                        onFilter={handleFilterChange}
                        add={false}
                        clearFilter={handleFilterClear}
                    >
                        <div className="d-flex justify-content-between flex-1 flex-wrap">
                            <div className="d-flex flex-wrap">
                                <div className="px-1">
                                    <TextInput
                                        text="BB SKU"
                                        name="sku"
                                        value={inputSku}
                                        onChange={(val) => {
                                            setInputSku(val.target.value);
                                        }}
                                        onPressEnter={handleFilterChange}
                                    />
                                </div>
                                <div className="px-1">
                                    <TextInput
                                        text="VV SKU"
                                        name="vv_sku"
                                        value={inputVVSku}
                                        onChange={(val) => {
                                            setInputVVSku(val.target.value);
                                        }}
                                        onPressEnter={handleFilterChange}
                                    />
                                </div>
                                <div className="px-1">
                                    <TextInput
                                        text="Title"
                                        name="title"
                                        value={inputTitle}
                                        onChange={(val) => {
                                            setInputTitle(val.target.value);
                                        }}
                                        onPressEnter={handleFilterChange}
                                    />
                                </div>
                                <div className="px-1" style={{ width: 300 }}>
                                    <div className="form-group">
                                        <label className="text-sm-label my-1">Category</label>
                                        <Select
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            value={inputCategory}
                                            onChange={(values) => {
                                                setInputCategory(values || []);
                                            }}
                                            placeholder="Select categories..."
                                            showSearch
                                            allowClear
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            notFoundContent={categoriesLoading ? "Loading..." : "No categories found"}
                                            loading={categoriesLoading}
                                            options={categoryOptions.filter(opt => opt.value !== "")}
                                            maxTagCount="responsive"
                                        />
                                    </div>
                                </div>
                                <div className="px-1" style={{ width: 250 }}>
                                    <div className="form-group">
                                        <label className="text-sm-label my-1">Brand</label>
                                        <Select
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            value={inputBrand}
                                            onChange={(values) => {
                                                setInputBrand(values || []);
                                            }}
                                            placeholder="Select brands..."
                                            showSearch
                                            allowClear
                                            filterOption={false}
                                            onSearch={(value) => setBrandSearchText(value)}
                                            notFoundContent={brandsLoading ? "Loading..." : "No brands found"}
                                            loading={brandsLoading}
                                            options={filteredBrandOptions.filter(opt => opt.value !== "")}
                                            maxTagCount="responsive"
                                        />
                                    </div>
                                </div>
                                <div className="px-1" style={{ width: 250 }}>
                                    <div className="form-group">
                                        <label className="text-sm-label my-1">Filter by Supplier</label>
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            placeholder="All Suppliers"
                                            style={{ width: '100%' }}
                                            value={inputSupplierFilter}
                                            onChange={(values) => {
                                                setInputSupplierFilter(values || []);
                                            }}
                                            allowClear
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={suppliers.map((supplier) => ({
                                                label: supplier.title || supplier.code,
                                                value: supplier.id
                                            }))}
                                            maxTagCount="responsive"
                                        />
                                    </div>
                                </div>

                                <div className="px-1">
                                    <SelectInput
                                        text="Old stock"
                                        name="old_stock"
                                        label="Old stock"
                                        value_return="light"
                                        options={[
                                            { label: "All", value: "" },
                                            { label: "Yes", value: "yes" },
                                            { label: "No", value: "no" },
                                        ]}
                                        value={inputOldStockFilter}
                                        onChange={(val) => {
                                            setInputOldStockFilter(val.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </TableFilterBox>

                    <div>
                        <div className="px-1">
                            <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label className="text-sm-label my-1">Destination Supplier</label>
                                <Select
                                    showSearch
                                    placeholder="Select destination supplier"
                                    style={{ width: '200px' }}
                                    value={selectedDestinationSupplier?.id || null}
                                    onChange={(value) => {
                                        if (value) {
                                            const supplier = suppliers.find(s => s.id === value);
                                            setSelectedDestinationSupplier(supplier);
                                        } else {
                                            setSelectedDestinationSupplier(null);
                                        }
                                    }}
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={suppliers.map((supplier) => ({
                                        label: supplier.title || supplier.code,
                                        value: supplier.id
                                    }))}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{
                        overflowX: 'auto',
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 350px)',
                    }}>
                        <ContentValidation
                            loader={productsLoading || productsFetching}
                            array={products?.data}
                            error={productsError}
                        >
                            <table className="table" style={{ marginBottom: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                    <tr className="text-muted">
                                        <th className="text-black">BB SKU</th>
                                        <th className="text-black">VV SKU</th>
                                        <th className="text-black">Title</th>
                                        <th className="text-black">Category</th>
                                        <th className="text-black">Brand</th>
                                        <th className="text-black">Cost price</th>
                                        <th className="text-black">Sell price</th>
                                        <th className="text-black">Technorama.lt price</th>
                                        {visibleSuppliers.map((supplier, index) => (
                                            <th
                                                key={supplier.id}
                                                className="text-black"
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSortSupplier(supplier.id)}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>
                                                        {supplier.title || supplier.code}
                                                    </span>
                                                    <span style={{
                                                        marginLeft: 4,
                                                        display: 'inline-flex',
                                                        flexDirection: 'column',
                                                        lineHeight: '1.2',
                                                        fontSize: '10px'
                                                    }}>
                                                        <span style={{ opacity: sortSupplier === supplier.id && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                                                        <span style={{ opacity: sortSupplier === supplier.id && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                        <th className="text-black">Total QTY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData?.map?.((product, productIndex) => (
                                        <React.Fragment key={product.id}>
                                            <ProductRow
                                                product={product}
                                                suppliers={suppliers}
                                                visibleSuppliers={visibleSuppliers}
                                                isExpanded={expandedRows.has(product.id)}
                                                onToggleExpand={() => toggleRowExpansion(product.id)}
                                                onSupplierQuantityUpdate={handleSupplierQuantityUpdate}
                                                refetchProducts={refetchProducts}
                                                rowIndex={productIndex}
                                                selectedDestinationSupplier={selectedDestinationSupplier}
                                            />
                                            {expandedRows.has(product.id) && (
                                                <ExpandedProductRow
                                                    product={product}
                                                    suppliers={visibleSuppliers}
                                                    allSuppliers={suppliers}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </ContentValidation>
                    </div>
                    <Pagination
                        setPagination={setPage}
                        page={page}
                        totals={totals}
                        pageCount={perPage}
                    />
                </ContentBlock>
            </>
        </Container>
    );
};

const ProductRow = ({ product, suppliers, visibleSuppliers, isExpanded, onToggleExpand, onSupplierQuantityUpdate, refetchProducts, rowIndex, selectedDestinationSupplier }) => {
    const totalQty = product.product_suppliers?.reduce((sum, ps) => sum + (ps.quantity || 0), 0) || 0;

    const handleProductMigration = () => {
        refetchProducts();
    };

    const rowBg = rowIndex % 2 === 1 ? '#f8f9fa' : 'white';

    return (
        <tr className="align-middle" style={{ backgroundColor: rowBg }}>
            <td className="align-middle">{product.identifier}</td>
            <td className="align-middle">{product.vv_sku}</td>
            <td className="align-middle">
                <div className="d-flex align-items-center justify-content-between">
                    <span>
                        {product.title}
                    </span>
                    <button
                        className="btn d-flex align-items-center"
                        onClick={onToggleExpand}
                        style={{
                            backgroundColor: '#009BDA',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '1px 3px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            minWidth: '30px',
                            minHeight: '30px',
                            maxWidth: '30px',
                            maxHeight: '30px',
                            textAlign: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <span className="free">
                            <svg
                                stroke="currentColor"
                                fill="currentColor"
                                strokeWidth="0"
                                viewBox="0 0 448 512"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{
                                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s"
                                }}
                            >
                                <path d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
                            </svg>
                        </span>
                    </button>
                </div>
            </td>
            <td className="align-middle">{product.category}</td>
            <td className="align-middle">{product.brand?.title || product.brand?.name || '-'}</td>
            <td className="align-middle">{product.product_price}€</td>
            <td className="align-middle">{product.sell_price}€</td>
            <td className="align-middle">{product.technorama_price}€</td>
            {visibleSuppliers.map((supplier, index) => {
                const supplierData = product.product_suppliers?.find(ps => ps.id === supplier.id);
                const quantity = supplierData?.quantity || 0;
                const pendingQuantity = supplierData?.pending_quantity || 0;
                const oldStock = supplierData?.old || false;

                return (
                    <td key={supplier.id} className="align-middle">
                        <div className="d-flex align-items-center">
                            <span style={{ color: oldStock ? 'red' : 'inherit' }}>{pendingQuantity > 0 ? `${pendingQuantity}/${quantity}` : quantity}</span>
                            <MigrateOutPopover
                                productSupplier={supplierData || { id: supplier.id, supplier_id: supplier.id, product_id: product.id, quantity: 0 }}
                                productId={product.id}
                                onSave={handleProductMigration}
                                supplierName={supplier.title || supplier.code}
                                hasPendingQuantity={pendingQuantity > 0}
                                hasQuantity={quantity > 0}
                                selectedDestinationSupplier={selectedDestinationSupplier}
                                productSuppliers={product.product_suppliers}
                            />
                        </div>
                    </td>
                );
            })}
            <td className="align-middle">
                <strong>{totalQty}</strong>
            </td>
        </tr>
    );
};

// Expandable detail row component
const ExpandedProductRow = ({ product, suppliers, allSuppliers }) => {
    const [warehouseData, setWarehouseData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Use allSuppliers if provided, otherwise use suppliers
    const suppliersList = allSuppliers || suppliers || [];
    const visibleSuppliersList = suppliers || [];
    const colSpan = 6 + visibleSuppliersList.length;

    React.useEffect(() => {
        const fetchWarehouseData = async () => {
            setLoading(true);
            setError(null);
            try {
                let url = `/api/v1/products/${product.id}/warehouse_data`;
                if (allSuppliers && allSuppliers.length > 0) {
                    const supplierIds = allSuppliers.map(s => s.id);
                    url += `?supplier_ids[]=${supplierIds.join('&supplier_ids[]=')}`;
                }
                const response = await axios.get(url);
                setWarehouseData(response.data || []);
            } catch (err) {
                console.error('Error fetching warehouse data:', err);
                setError(err);
                setWarehouseData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchWarehouseData();
    }, [product.id, allSuppliers]);

    const getTotalQuantity = (warehouseItem) => {
        return warehouseItem?.quantities?.reduce((sum, qty) => sum + (qty.quantity || 0), 0) || 0;
    };

    const getSupplierData = (warehouseItem, supplierId) => {
        return warehouseItem?.quantities?.find(qty => qty.supplier_id === supplierId)?.quantity || 0;
    };
    const getSupplierDataCost = (warehouseItem, supplierId) => {
        return warehouseItem?.quantities?.find(qty => qty.supplier_id === supplierId)?.cost_price || '-';
    };

    if (loading) {
        return (
            <tr>
                <td colSpan={colSpan} style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    Loading warehouse data...
                </td>
            </tr>
        );
    }

    if (error) {
        return (
            <tr>
                <td colSpan={colSpan} style={{ padding: '20px', textAlign: 'center', backgroundColor: '#fff3cd', color: '#856404' }}>
                    Error loading warehouse data
                </td>
            </tr>
        );
    }

    if (!warehouseData || warehouseData.length === 0) {
        return (
            <tr>
                <td colSpan={colSpan} style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    No warehouse data available for this product
                </td>
            </tr>
        );
    }

    return (
        <>
            {/* Header row for expanded data */}
            <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fff' }}></td>
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600' }}>Purchase date</td>
                {visibleSuppliersList.map(supplier => (
                    <td key={supplier.id} style={{ padding: '8px', fontSize: '12px', fontWeight: '600', textAlign: 'center' }}>
                        {supplier.title || supplier.code}
                    </td>
                ))}
                <td style={{ padding: '8px', fontSize: '12px', fontWeight: '600', textAlign: 'center' }}>Total QTY</td>
            </tr>
            {/* Data rows */}
            {Array.isArray(warehouseData) && warehouseData.map((item, index) => (
                <tr key={index} style={{ backgroundColor: '#f8f9fa' }}>
                    <td style={{ padding: '8px', fontSize: '13px' }}>

                    </td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px', backgroundColor: '#fff' }}></td>
                    <td style={{ padding: '8px', fontSize: '13px' }}>
                        <strong style={{ whiteSpace: "nowrap" }}>{dayjs(item.date).format('YYYY-MM-DD')}</strong>
                    </td>
                    {(() => {
                        const isOld = dayjs().diff(dayjs(item.date), 'day') > 180;
                        return visibleSuppliersList.map(supplier => {
                            const qty = getSupplierData(item, supplier.id);
                            const costPrice = getSupplierDataCost(item, supplier.id);
                            const color = isOld && qty > 0 ? 'red' : undefined;
                            return (
                                <td key={supplier.id} style={{ padding: '8px', fontSize: '13px', textAlign: 'center', color }}>
                                    {qty > 0 ? <strong>{qty}</strong> : qty}
                                    {qty > 0 && <div style={{ fontSize: '10px' }}>{costPrice}€</div>}
                                </td>
                            );
                        });
                    })()}
                    {(() => {
                        const isOld = dayjs().diff(dayjs(item.date), 'day') > 180;
                        const total = getTotalQuantity(item);
                        return (
                            <td style={{ padding: '8px', fontSize: '13px', fontWeight: 'bold', textAlign: 'center', color: isOld && total > 0 ? 'red' : undefined }}>
                                {total}
                            </td>
                        );
                    })()}
                </tr>
            ))}
        </>
    );
};



const VVStockProductsPage = () => {
    return (
        <QueryClientProvider client={queryVVStockProductsClient}>
            <VVStockProductsPageContent />
        </QueryClientProvider>
    );
};

export default VVStockProductsPage;
